import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bomberosAlertaAPI, bomberosCompaniaAPI } from '../../lib/api';
import { useBomberosPermisos } from '../../hooks/useBomberosPermisos';
import { BomberosHeader } from '../../components/BomberosHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
    ArrowLeft,
    Bell,
    AlertTriangle,
    CheckCircle,
    Truck,
    HardHat,
    GraduationCap,
    User,
    RefreshCw,
    Filter,
    Clock,
} from 'lucide-react';

const tipoConfig = {
    documento_vencido: { icon: Truck, color: 'text-yellow-600 bg-yellow-100', label: 'Documento Vencido' },
    epp_vencido: { icon: HardHat, color: 'text-orange-600 bg-orange-100', label: 'EPP Vencido' },
    capacitacion_vencida: { icon: GraduationCap, color: 'text-purple-600 bg-purple-100', label: 'Capacitación Vencida' },
    brecha_competencia: { icon: User, color: 'text-red-600 bg-red-100', label: 'Brecha de Competencia' },
};

const prioridadConfig = {
    critica: { color: 'bg-red-500 text-white', label: 'Crítica' },
    alta: { color: 'bg-orange-500 text-white', label: 'Alta' },
    media: { color: 'bg-yellow-500 text-black', label: 'Media' },
    baja: { color: 'bg-blue-500 text-white', label: 'Baja' },
};

export default function BomberosAlertasPage() {
    const navigate = useNavigate();
    const { tienePermiso } = useBomberosPermisos();
    const [loading, setLoading] = useState(true);
    const [generando, setGenerando] = useState(false);
    const [alertas, setAlertas] = useState([]);
    const [companias, setCompanias] = useState([]);
    const [filterCompania, setFilterCompania] = useState('all');
    const [filterTipo, setFilterTipo] = useState('all');
    const [filterResuelta, setFilterResuelta] = useState('false');

    useEffect(() => {
        loadCompanias();
    }, []);

    useEffect(() => {
        loadAlertas();
    }, [filterCompania, filterTipo, filterResuelta]);

    const loadCompanias = async () => {
        try {
            const res = await bomberosCompaniaAPI.getAll();
            setCompanias(res.data);
        } catch (err) {
            console.error('Error loading companias:', err);
        }
    };

    const loadAlertas = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filterCompania !== 'all') params.compania_id = filterCompania;
            if (filterTipo !== 'all') params.tipo = filterTipo;
            if (filterResuelta !== 'all') params.resuelta = filterResuelta === 'true';
            
            const res = await bomberosAlertaAPI.getAll(params);
            setAlertas(res.data);
        } catch (err) {
            console.error('Error loading alertas:', err);
            toast.error('Error al cargar alertas');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerarAlertas = async () => {
        setGenerando(true);
        try {
            const res = await bomberosAlertaAPI.generar();
            toast.success(`Se generaron ${res.data.alertas_generadas} alertas`);
            loadAlertas();
        } catch (err) {
            toast.error('Error al generar alertas');
        } finally {
            setGenerando(false);
        }
    };

    const handleResolver = async (alertaId) => {
        try {
            await bomberosAlertaAPI.resolver(alertaId);
            toast.success('Alerta marcada como resuelta');
            loadAlertas();
        } catch (err) {
            toast.error('Error al resolver alerta');
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('es-CL', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Stats
    const stats = {
        total: alertas.length,
        critica: alertas.filter(a => a.prioridad === 'critica' && !a.resuelta).length,
        alta: alertas.filter(a => a.prioridad === 'alta' && !a.resuelta).length,
        media: alertas.filter(a => a.prioridad === 'media' && !a.resuelta).length,
        resueltas: alertas.filter(a => a.resuelta).length,
    };

    if (loading && alertas.length === 0) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <div className="grid grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
                </div>
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <>
            <BomberosHeader />
            <div className="space-y-6 p-6 pt-20 md:pt-20">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/bomberos/dashboard')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Bell className="h-6 w-6" />
                            Alertas del Sistema
                        </h1>
                        <p className="text-muted-foreground">Gestión de alertas y vencimientos</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={loadAlertas}>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Actualizar
                    </Button>
                    {tienePermiso('generar_alertas') && (
                        <Button onClick={handleGenerarAlertas} disabled={generando}>
                            <AlertTriangle className="h-4 w-4 mr-2" />
                            {generando ? 'Generando...' : 'Generar Alertas'}
                        </Button>
                    )}
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-5 gap-4">
                <Card className="border">
                    <CardContent className="pt-4 pb-4 text-center">
                        <Bell className="h-5 w-5 mx-auto mb-1 text-gray-500" />
                        <p className="text-2xl font-bold">{stats.total}</p>
                        <p className="text-xs text-muted-foreground">Total</p>
                    </CardContent>
                </Card>
                <Card className="border border-red-200">
                    <CardContent className="pt-4 pb-4 text-center">
                        <AlertTriangle className="h-5 w-5 mx-auto mb-1 text-red-500" />
                        <p className="text-2xl font-bold text-red-600">{stats.critica}</p>
                        <p className="text-xs text-muted-foreground">Críticas</p>
                    </CardContent>
                </Card>
                <Card className="border border-orange-200">
                    <CardContent className="pt-4 pb-4 text-center">
                        <AlertTriangle className="h-5 w-5 mx-auto mb-1 text-orange-500" />
                        <p className="text-2xl font-bold text-orange-600">{stats.alta}</p>
                        <p className="text-xs text-muted-foreground">Alta Prioridad</p>
                    </CardContent>
                </Card>
                <Card className="border border-yellow-200">
                    <CardContent className="pt-4 pb-4 text-center">
                        <Clock className="h-5 w-5 mx-auto mb-1 text-yellow-500" />
                        <p className="text-2xl font-bold text-yellow-600">{stats.media}</p>
                        <p className="text-xs text-muted-foreground">Media Prioridad</p>
                    </CardContent>
                </Card>
                <Card className="border border-green-200">
                    <CardContent className="pt-4 pb-4 text-center">
                        <CheckCircle className="h-5 w-5 mx-auto mb-1 text-green-500" />
                        <p className="text-2xl font-bold text-green-600">{stats.resueltas}</p>
                        <p className="text-xs text-muted-foreground">Resueltas</p>
                    </CardContent>
                </Card>
            </div>

            {/* Filters */}
            <Card className="border-2">
                <CardContent className="pt-6">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1">
                            <Select value={filterCompania} onValueChange={setFilterCompania}>
                                <SelectTrigger data-testid="filter-compania">
                                    <SelectValue placeholder="Compañía" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todas las Compañías</SelectItem>
                                    {companias.map(c => (
                                        <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex-1">
                            <Select value={filterTipo} onValueChange={setFilterTipo}>
                                <SelectTrigger data-testid="filter-tipo">
                                    <SelectValue placeholder="Tipo" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todos los Tipos</SelectItem>
                                    <SelectItem value="documento_vencido">Documento Vencido</SelectItem>
                                    <SelectItem value="epp_vencido">EPP Vencido</SelectItem>
                                    <SelectItem value="capacitacion_vencida">Capacitación Vencida</SelectItem>
                                    <SelectItem value="brecha_competencia">Brecha de Competencia</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex-1">
                            <Select value={filterResuelta} onValueChange={setFilterResuelta}>
                                <SelectTrigger data-testid="filter-resuelta">
                                    <SelectValue placeholder="Estado" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todas</SelectItem>
                                    <SelectItem value="false">Pendientes</SelectItem>
                                    <SelectItem value="true">Resueltas</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Alertas List */}
            <Card className="border-2">
                <CardHeader>
                    <CardTitle className="text-lg">Listado de Alertas</CardTitle>
                </CardHeader>
                <CardContent>
                    {alertas.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
                            <p>No hay alertas con los filtros aplicados</p>
                            <p className="text-sm mt-2">Haz clic en "Generar Alertas" para verificar vencimientos</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {alertas.map((alerta) => {
                                const tipo = tipoConfig[alerta.tipo] || { icon: AlertTriangle, color: 'text-gray-600 bg-gray-100', label: alerta.tipo };
                                const TipoIcon = tipo.icon;
                                const prioridad = prioridadConfig[alerta.prioridad] || prioridadConfig.media;

                                return (
                                    <div
                                        key={alerta.id}
                                        className={`p-4 rounded-lg border ${alerta.resuelta ? 'opacity-60 bg-muted/30' : 'bg-background'}`}
                                        data-testid={`alerta-${alerta.id}`}
                                    >
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex items-start gap-3">
                                                <div className={`p-2 rounded-lg ${tipo.color}`}>
                                                    <TipoIcon className="h-5 w-5" />
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <h4 className="font-medium">{alerta.titulo}</h4>
                                                        <Badge className={prioridad.color}>
                                                            {prioridad.label}
                                                        </Badge>
                                                        {alerta.resuelta && (
                                                            <Badge variant="outline" className="text-green-600">
                                                                <CheckCircle className="h-3 w-3 mr-1" />
                                                                Resuelta
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-muted-foreground">{alerta.mensaje}</p>
                                                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                                        <span>{tipo.label}</span>
                                                        <span>·</span>
                                                        <span>{formatDate(alerta.created_at)}</span>
                                                        {alerta.fecha_resolucion && (
                                                            <>
                                                                <span>·</span>
                                                                <span>Resuelta: {formatDate(alerta.fecha_resolucion)}</span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            {!alerta.resuelta && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleResolver(alerta.id)}
                                                    data-testid={`resolver-${alerta.id}`}
                                                >
                                                    <CheckCircle className="h-4 w-4 mr-1" />
                                                    Resolver
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
        </>
    );
}
